import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { 
    Search, Home, Footprints, Heart, Car, Star, Shield, 
    Clock, MapPin, ChevronRight, PawPrint, Check 
} from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';

const services = [
    { 
        type: 'hosting', 
        icon: Home, 
        title: 'Hospedagem', 
        description: 'Deixe seu pet em uma casa acolhedora',
        color: 'bg-blue-50 text-blue-600'
    },
    { 
        type: 'walking', 
        icon: Footprints, 
        title: 'Passeios', 
        description: 'Passeios seguros e divertidos',
        color: 'bg-green-50 text-green-600'
    },
    { 
        type: 'daycare', 
        icon: Heart, 
        title: 'Cuidados Diários', 
        description: 'Cuidados especiais enquanto você trabalha',
        color: 'bg-amber-50 text-amber-600'
    },
    { 
        type: 'taxi_dog', 
        icon: Car, 
        title: 'Táxi Dog', 
        description: 'Transporte seguro para seu pet',
        color: 'bg-pink-50 text-pink-600'
    },
];

const features = [
    { icon: Shield, title: 'Verificados', description: 'Todos os prestadores passam por verificação' },
    { icon: Star, title: 'Avaliações', description: 'Veja o que outros clientes dizem' },
    { icon: Clock, title: 'Flexível', description: 'Agende no horário que precisar' },
];

const howItWorks = [
    { step: 1, title: 'Busque', description: 'Encontre o serviço ideal na sua região' },
    { step: 2, title: 'Reserve', description: 'Escolha a data e confirme a reserva' },
    { step: 3, title: 'Aproveite', description: 'Seu pet em boas mãos, você tranquilo' },
];

export default function Landing() {
    const navigate = useNavigate();
    const [searchCity, setSearchCity] = useState('');

    const handleSearch = (serviceType = null) => {
        const params = new URLSearchParams();
        if (searchCity) params.set('city', searchCity);
        if (serviceType) params.set('type', serviceType);
        navigate(`/services?${params.toString()}`);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <section className="hero-gradient py-16 md:py-24" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="space-y-6 animate-fade-in">
                            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                                <PawPrint className="w-4 h-4 text-[#4A90B2]" />
                                <span className="text-sm font-medium text-slate-600">Onde seu pet é bem recebido</span>
                            </div>
                            
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-800 font-['Nunito'] tracking-tight leading-tight">
                                Cuidado de verdade para seu{' '}
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4A90B2] to-[#7AB89C]">
                                    melhor amigo
                                </span>
                            </h1>
                            
                            <p className="text-lg text-slate-600 max-w-lg">
                                Conectamos donos de pets a anfitriões, cuidadores e motoristas verificados. 
                                Encontre o serviço perfeito perto de você.
                            </p>

                            {/* Search Box */}
                            <div className="bg-white p-4 rounded-2xl shadow-lg max-w-lg" data-testid="search-box">
                                <div className="flex gap-3">
                                    <div className="relative flex-1">
                                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <Input
                                            placeholder="Digite sua cidade..."
                                            value={searchCity}
                                            onChange={(e) => setSearchCity(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            className="pl-10 h-12 rounded-xl border-slate-200"
                                            data-testid="search-city-input"
                                        />
                                    </div>
                                    <Button 
                                        onClick={() => handleSearch()}
                                        className="btn-primary h-12 px-6"
                                        data-testid="search-btn"
                                    >
                                        <Search className="w-5 h-5" />
                                    </Button>
                                </div>
                            </div>

                            {/* Pet Photos */}
                            <div className="flex gap-4 pt-6">
                                <div className="flex-1 overflow-hidden rounded-2xl shadow-lg" style={{ backgroundColor: '#2a3a5c' }}>
                                    <img
                                        src="https://customer-assets.emergentagent.com/job_ba48a296-a556-457c-bc05-0d8131137607/artifacts/nibw0qra_IMG_4688.jpeg"
                                        alt="Gato sentado olhando para a câmera"
                                        className="w-full h-48 sm:h-56 object-cover object-[center_15%]"
                                        style={{ filter: 'brightness(1.05) contrast(1.05) saturate(1.08)' }}
                                    />
                                </div>
                                <div className="flex-1 overflow-hidden rounded-2xl shadow-lg">
                                    <img
                                        src="https://customer-assets.emergentagent.com/job_e074a728-b10a-4818-ad73-c15926b20c9a/artifacts/lnsrx4rt_1452d23c-f65c-499a-b08a-30fbb42d555c.jpeg"
                                        alt="Pitbull relaxando no sofá"
                                        className="w-full h-48 sm:h-56 object-cover object-center"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Hero Image */}
                        <div className="relative hidden md:block animate-fade-in stagger-2">
                            <div className="absolute -inset-4 bg-gradient-to-r from-[#4A90B2]/20 to-[#7AB89C]/20 rounded-[32px] blur-2xl"></div>
                            <img
                                src="https://images.unsplash.com/photo-1753685722861-e57bf709b71f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                                alt="Pessoa abraçando cachorro"
                                className="relative rounded-3xl shadow-2xl w-full object-cover"
                            />
                            {/* Floating card */}
                            <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl animate-pulse-soft">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                        <Check className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-800">Verificado</p>
                                        <p className="text-sm text-slate-500">Anfitrião aprovado</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-16 md:py-24 bg-white" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 font-['Nunito'] mb-4">
                            Serviços para seu pet
                        </h2>
                        <p className="text-slate-600 max-w-2xl mx-auto">
                            Encontre o serviço ideal para as necessidades do seu companheiro
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {services.map((service, index) => (
                            <Card
                                key={service.type}
                                className="card-service animate-fade-in"
                                style={{ animationDelay: `${index * 0.1}s` }}
                                onClick={() => handleSearch(service.type)}
                                data-testid={`service-type-${service.type}`}
                            >
                                <div className={`service-icon ${service.color} mb-4`}>
                                    <service.icon className="w-6 h-6" />
                                </div>
                                <h3 className="font-bold text-lg text-slate-800 mb-2">{service.title}</h3>
                                <p className="text-sm text-slate-500 mb-4">{service.description}</p>
                                <div className="flex items-center text-[#4A90B2] font-medium text-sm">
                                    Ver opções <ChevronRight className="w-4 h-4 ml-1" />
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it Works */}
            <section className="py-16 md:py-24 bg-slate-50" data-testid="how-it-works-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 font-['Nunito'] mb-4">
                            Como funciona
                        </h2>
                        <p className="text-slate-600">Simples, rápido e seguro</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {howItWorks.map((item, index) => (
                            <div 
                                key={item.step} 
                                className="text-center animate-fade-in"
                                style={{ animationDelay: `${index * 0.15}s` }}
                            >
                                <div className="w-16 h-16 bg-gradient-to-r from-[#4A90B2] to-[#7AB89C] rounded-2xl flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl shadow-lg">
                                    {item.step}
                                </div>
                                <h3 className="font-bold text-xl text-slate-800 mb-2">{item.title}</h3>
                                <p className="text-slate-500">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="py-16 md:py-24 bg-white" data-testid="features-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 font-['Nunito'] mb-6">
                                Por que escolher o RecepPet?
                            </h2>
                            <div className="space-y-6">
                                {features.map((feature, index) => (
                                    <div 
                                        key={feature.title} 
                                        className="flex gap-4 animate-fade-in"
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                    >
                                        <div className="w-12 h-12 bg-[#F0F9FF] rounded-xl flex items-center justify-center flex-shrink-0">
                                            <feature.icon className="w-6 h-6 text-[#4A90B2]" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-slate-800 mb-1">{feature.title}</h3>
                                            <p className="text-slate-500">{feature.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1665118435795-3fda8dec879b?crop=entropy&cs=srgb&fm=jpg&q=85&w=500"
                                alt="Pessoa passeando com cachorro"
                                className="rounded-3xl shadow-xl"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 md:py-24 bg-gradient-to-r from-[#4A90B2] to-[#7AB89C]" data-testid="cta-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white font-['Nunito'] mb-4">
                        Pronto para começar?
                    </h2>
                    <p className="text-white/90 mb-8 max-w-2xl mx-auto">
                        Cadastre-se agora e encontre o melhor cuidado para seu pet
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button
                            onClick={() => navigate('/register')}
                            className="bg-white text-[#4A90B2] hover:bg-slate-100 rounded-full px-8 py-3 font-bold shadow-lg"
                            data-testid="cta-register-btn"
                        >
                            Criar conta grátis
                        </Button>
                        <Button
                            onClick={() => navigate('/register?type=provider')}
                            className="bg-transparent border-2 border-white !text-white hover:bg-white/10 rounded-full px-8 py-3 font-bold"
                            data-testid="cta-provider-btn"
                        >
                            Quero ser prestador
                        </Button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer />
        </div>
    );
}
